

import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/DefaultSelectDropdown';
import { Checkbox } from '@/components/ui/checkbox';
import IconProvider from '@/components/ui/IconProvider';
import { GlossaryBlueprint } from '@/types-games';
import { ObjectItem, updateObject } from '@/lib/stores/objectsStore';
import {
  detectFieldChangesForObjects,
  getAggregatedTypeConflicts,
  syncObjectToBlueprint,
  isTypesToTextConversion,
  ObjectChangeSummary,
  FieldChange,
  TypeConflictDecision,
  TypeElementLookup,
} from '@/lib/utils/objectSync';
import { useStatusWorkflows } from '@/hooks/useStatusWorkflows';
import { useStatusData } from '@/hooks/useStatusData';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { useToast } from '@/hooks/use-toast';
import { useGlossaryDataContext } from '@/providers/ObjectsDataProvider';
import { useTypesDataContext } from '@/providers/TypesDataProvider';
import { getStatusColor } from '@/lib/utils/statusColors';

// Extended decision type for types→text conversions
type ExtendedDecision = 'clear' | 'convert' | 'keep' | 'convert_id' | 'convert_name';

interface TemplateSyncWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  blueprint: GlossaryBlueprint;
  outOfSyncObjects: ObjectItem[];
  onComplete?: () => void;
}

type WizardStep = 'review' | 'status' | 'confirm';

const STEPS: WizardStep[] = ['review', 'status', 'confirm'];

const STEP_TITLES: Record<WizardStep, string> = {
  review: 'Review Changes',
  status: 'Set Status',
  confirm: 'Confirm & Execute',
};

export function TemplateSyncWizard({
  open,
  onOpenChange,
  blueprint,
  outOfSyncObjects,
  onComplete,
}: TemplateSyncWizardProps) {
  const { toast } = useToast();
  const { selectedGame } = useSimplifiedUserData();
  const { loadData: refreshObjects } = useGlossaryDataContext();
  const { types } = useTypesDataContext();
  
  // Ensure status data is loaded
  useStatusData();
  const statusWorkflows = useStatusWorkflows({ gameId: selectedGame?.id });
  
  const [currentStep, setCurrentStep] = useState<WizardStep>('review');
  const [typeDecisions, setTypeDecisions] = useState<Map<string, ExtendedDecision>>(new Map());
  
  // Create a type element lookup function
  const typeElementLookup: TypeElementLookup = useMemo(() => {
    // Build a map of all type elements for quick lookup
    const elementMap = new Map<string, { id: string; name: string }>();
    types.forEach(type => {
      if (type.data) {
        type.data.forEach(element => {
          elementMap.set(element.id, { id: element.id, name: element.name });
        });
      }
    });
    return (elementId: string) => elementMap.get(elementId);
  }, [types]);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Analyze changes for all objects
  const changeSummaries = useMemo(() => {
    return detectFieldChangesForObjects(outOfSyncObjects, blueprint);
  }, [outOfSyncObjects, blueprint]);

  // Get aggregated type conflicts
  const aggregatedConflicts = useMemo(() => {
    return getAggregatedTypeConflicts(changeSummaries);
  }, [changeSummaries]);

  // Check if there are any type conflicts
  const hasTypeConflicts = aggregatedConflicts.length > 0;

  // Get available statuses
  const availableStatuses = useMemo(() => {
    return Object.entries(statusWorkflows.objects || {}).map(([key, value]) => ({
      key,
      color: (value as { color: string }).color,
    }));
  }, [statusWorkflows.objects]);

  // Calculate totals for summary
  const totals = useMemo(() => {
    return changeSummaries.reduce(
      (acc, summary) => ({
        added: acc.added + summary.addedFields.length,
        removed: acc.removed + summary.removedFields.length,
        typeChanged: acc.typeChanged + summary.typeChangedFields.length,
      }),
      { added: 0, removed: 0, typeChanged: 0 }
    );
  }, [changeSummaries]);

  const handleNext = () => {
    const currentIndex = STEPS.indexOf(currentStep);
    if (currentIndex < STEPS.length - 1) {
      setCurrentStep(STEPS[currentIndex + 1]);
    }
  };

  const handleBack = () => {
    const currentIndex = STEPS.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(STEPS[currentIndex - 1]);
    }
  };

  const handleTypeDecision = (fieldId: string, decision: ExtendedDecision) => {
    setTypeDecisions(prev => {
      const next = new Map(prev);
      next.set(fieldId, decision);
      return next;
    });
  };

  const handleApplyToAll = (decision: ExtendedDecision) => {
    setTypeDecisions(prev => {
      const next = new Map(prev);
      aggregatedConflicts.forEach(conflict => {
        next.set(conflict.fieldId, decision);
      });
      return next;
    });
  };

  const handleExecute = async () => {
    if (!selectedStatus) {
      toast({
        title: 'Status Required',
        description: 'Please select a status for the updated objects.',
        variant: 'destructive',
      });
      return;
    }

    setIsUpdating(true);
    try {
      let successCount = 0;
      let errorCount = 0;

      // Build type decisions array
      const decisions: TypeConflictDecision[] = Array.from(typeDecisions.entries()).map(
        ([fieldId, action]) => ({ fieldId, action })
      );

      // Update each object
      for (const obj of outOfSyncObjects) {
        try {
          // Sync the object with type decisions, new status, and type element lookup
          const syncedObject = syncObjectToBlueprint(obj, blueprint, decisions, selectedStatus, typeElementLookup);

          // Skip objects without a type
          if (!syncedObject.type) {
            console.warn(`Skipping object ${obj.id}: missing type field`);
            errorCount++;
            continue;
          }
          
          // Skip objects without game_id (shouldn't happen but defensive check)
          if (!syncedObject.game_id) {
            console.warn(`Skipping object ${obj.id}: missing game_id field`);
            errorCount++;
            continue;
          }

          // Save the updated object
          // Explicitly include game_id to ensure it's passed for string creation
          await updateObject(
            {
              ...syncedObject,
              id: syncedObject.id,
              game_id: syncedObject.game_id,
              name: syncedObject.name,
              type: syncedObject.type,
              data: syncedObject.data,
              tags: syncedObject.tags,
              blueprint_id: syncedObject.blueprint_id,
              blueprint_version: syncedObject.blueprint_version,
              identifiers: syncedObject.identifiers,
              folder: syncedObject.folder,
              status: syncedObject.status as 'draft' | 'published' | undefined,
            },
            'patch',
            undefined,
            undefined
          );
          successCount++;
        } catch (error) {
          console.error(`Failed to update object ${obj.id}:`, error);
          errorCount++;
        }
      }

      // Refresh the objects list
      await refreshObjects();

      // Show result toast
      if (errorCount === 0) {
        toast({
          title: 'Success',
          description: `Updated ${successCount} object(s) to match the current template version.`,
        });
      } else {
        toast({
          title: 'Partially Complete',
          description: `Updated ${successCount} object(s). ${errorCount} object(s) failed to update.`,
          variant: 'default',
        });
      }

      onOpenChange(false);
      onComplete?.();
    } catch (error) {
      console.error('Error updating objects:', error);
      toast({
        title: 'Error',
        description: 'Failed to update objects. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 'review':
        // All type conflicts must have a decision before proceeding
        return !hasTypeConflicts || aggregatedConflicts.every(c => typeDecisions.has(c.fieldId));
      case 'status':
        return !!selectedStatus;
      case 'confirm':
        return true;
      default:
        return false;
    }
  };

  const getStepNumber = () => {
    return STEPS.indexOf(currentStep) + 1;
  };

  const getTotalSteps = () => {
    return STEPS.length;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col ">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Step {getStepNumber()} of {getTotalSteps()}
            </span>
          </div>
          <DialogTitle>{STEP_TITLES[currentStep]}</DialogTitle>
          <DialogDescription>
            {currentStep === 'review' && (hasTypeConflicts 
              ? 'Review changes and choose how to handle fields that changed type.'
              : 'Review the changes that will be applied to your objects.')}
            {currentStep === 'status' && 'Select a status to apply to all updated objects.'}
            {currentStep === 'confirm' && 'Review your choices and confirm the update.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden py-4">
          <div className="h-full overflow-y-auto pr-2">
            {currentStep === 'review' && (
              <ReviewStep
                changeSummaries={changeSummaries}
                totals={totals}
                objectCount={outOfSyncObjects.length}
                hasTypeConflicts={hasTypeConflicts}
                aggregatedConflicts={aggregatedConflicts}
                typeDecisions={typeDecisions}
                onTypeDecision={handleTypeDecision}
                onApplyToAll={handleApplyToAll}
              />
            )}
            {currentStep === 'status' && (
              <StatusStep
                availableStatuses={availableStatuses}
                selectedStatus={selectedStatus}
                onStatusChange={setSelectedStatus}
              />
            )}
            {currentStep === 'confirm' && (
              <ConfirmStep
                objectCount={outOfSyncObjects.length}
                totals={totals}
                hasConflicts={hasTypeConflicts}
                conflictCount={aggregatedConflicts.length}
                selectedStatus={selectedStatus}
                statusColor={availableStatuses.find(s => s.key === selectedStatus)?.color}
              />
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0 border-t pt-4">
          <div className="flex justify-between w-full">
            <Button
              variant="outline"
              onClick={() => (currentStep === 'review' ? onOpenChange(false) : handleBack())}
              disabled={isUpdating}
            >
              {currentStep === 'review' ? 'Cancel' : 'Back'}
            </Button>
            <div className="flex gap-2">
              {currentStep !== 'confirm' ? (
                <Button onClick={handleNext} disabled={!canProceed()}>
                  Next
                  <IconProvider iconstring="fa-arrow-right" className="ml-2" size={12} />
                </Button>
              ) : (
                <Button onClick={handleExecute} disabled={isUpdating || !canProceed()}>
                  {isUpdating ? (
                    <>
                      <IconProvider iconstring="fa-spinner" className="animate-spin mr-2" size={12} />
                      Updating...
                    </>
                  ) : (
                    <>
                      <IconProvider iconstring="fa-check" className="mr-2" size={12} />
                      Update {outOfSyncObjects.length} Object{outOfSyncObjects.length !== 1 ? 's' : ''}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Step 1: Review Changes (with inline conflict handling)
interface ReviewStepProps {
  changeSummaries: ObjectChangeSummary[];
  totals: { added: number; removed: number; typeChanged: number };
  objectCount: number;
  hasTypeConflicts: boolean;
  aggregatedConflicts: Array<FieldChange & { affectedObjectCount: number }>;
  typeDecisions: Map<string, ExtendedDecision>;
  onTypeDecision: (fieldId: string, decision: ExtendedDecision) => void;
  onApplyToAll: (decision: ExtendedDecision) => void;
}

function ReviewStep({ 
  changeSummaries, 
  totals, 
  objectCount,
  hasTypeConflicts,
  aggregatedConflicts,
  typeDecisions,
  onTypeDecision,
  onApplyToAll,
}: ReviewStepProps) {
  const [expandedObjects, setExpandedObjects] = useState<Set<string>>(new Set());
  const [applyAllDecision, setApplyAllDecision] = useState<ExtendedDecision | ''>('');

  const toggleObject = (objectId: string) => {
    setExpandedObjects(prev => {
      const next = new Set(prev);
      if (next.has(objectId)) {
        next.delete(objectId);
      } else {
        next.add(objectId);
      }
      return next;
    });
  };

  const handleApplyAll = () => {
    if (applyAllDecision) {
      onApplyToAll(applyAllDecision);
    }
  };

  // Count unresolved conflicts
  const unresolvedCount = aggregatedConflicts.filter(c => !typeDecisions.has(c.fieldId)).length;

  return (
    <div className="space-y-4">
      <div className="text-sm text-muted-foreground">
        {objectCount} object{objectCount !== 1 ? 's' : ''} will be updated
      </div>

      {/* Global Apply to All for Type Conflicts */}
      {hasTypeConflicts && (
        <div className="p-4 rounded bg-amber-500/10 border border-amber-500/30">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="font-medium text-amber-700 flex items-center gap-2">
                <IconProvider iconstring="fa-exclamation-triangle" size={14} />
                {totals.typeChanged} field{totals.typeChanged !== 1 ? 's' : ''} changed type
                {unresolvedCount > 0 && (
                  <span className="text-xs bg-amber-600 text-white px-2 py-0.5 rounded-full">
                    {unresolvedCount} unresolved
                  </span>
                )}
              </div>
              <div className="text-sm text-amber-600 mt-1">
                Choose how to handle the values for fields that changed type
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={applyAllDecision} onValueChange={(v: string) => setApplyAllDecision(v as ExtendedDecision)}>
                <SelectTrigger className="w-[180px] border rounded bg-background">
                  <SelectValue placeholder="Apply to all..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="clear">Clear (use default)</SelectItem>
                  <SelectItem value="convert">Attempt conversion</SelectItem>
                  <SelectItem value="keep">Keep original</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={handleApplyAll} disabled={!applyAllDecision}>
                Apply All
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Object List */}
      <div className="space-y-2">
        {changeSummaries.map(summary => {
          const isExpanded = expandedObjects.has(summary.object.id);
          const hasChanges = summary.hasChanges;
          const objectHasTypeConflicts = summary.typeChangedFields.length > 0;
          const objectUnresolvedCount = summary.typeChangedFields.filter(
            f => !typeDecisions.has(f.fieldId)
          ).length;

          return (
            <div key={summary.object.id} className="border rounded">
              <button
                onClick={() => toggleObject(summary.object.id)}
                className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors text-left"
              >
                <div className="flex items-center gap-2">
                  <IconProvider
                    iconstring={isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}
                    size={12}
                  />
                  <span className="font-medium">{summary.object.name}</span>
                  {objectHasTypeConflicts && objectUnresolvedCount > 0 && (
                    <span className="text-xs bg-amber-500 text-white px-1.5 py-0.5 rounded">
                      {objectUnresolvedCount} to resolve
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs">
                  {summary.addedFields.length > 0 && (
                    <span className="text-green-600">+{summary.addedFields.length} new</span>
                  )}
                  {summary.removedFields.length > 0 && (
                    <span className="text-red-600">-{summary.removedFields.length} removed</span>
                  )}
                  {summary.typeChangedFields.length > 0 && (
                    <span className="text-amber-600">{summary.typeChangedFields.length} changed</span>
                  )}
                  {!hasChanges && <span className="text-muted-foreground">No field changes</span>}
                </div>
              </button>

              {isExpanded && hasChanges && (
                <div className="border-t p-3 space-y-3">
                  {summary.addedFields.length > 0 && (
                    <FieldChangeList title="New Fields" fields={summary.addedFields} color="green" />
                  )}
                  {summary.removedFields.length > 0 && (
                    <FieldChangeList title="Removed Fields" fields={summary.removedFields} color="red" />
                  )}
                  {summary.typeChangedFields.length > 0 && (
                    <TypeChangeFieldList
                      fields={summary.typeChangedFields}
                      decisions={typeDecisions}
                      onDecision={onTypeDecision}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface FieldChangeListProps {
  title: string;
  fields: FieldChange[];
  color: 'green' | 'red';
}

function FieldChangeList({ title, fields, color }: FieldChangeListProps) {
  const colorClasses = {
    green: 'bg-green-500/10 border-green-500/30 text-green-700',
    red: 'bg-red-500/10 border-red-500/30 text-red-700',
  };

  return (
    <div>
      <div className="text-xs font-semibold text-muted-foreground mb-2">{title}</div>
      <div className="space-y-1">
        {fields.map(field => (
          <div
            key={field.fieldId}
            className={`text-sm p-2 rounded border ${colorClasses[color]}`}
          >
            <div className="font-medium">{field.fieldLabel}</div>
            <div className="text-xs opacity-75">
              Section: {field.sectionTitle}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Type change fields with inline decision controls
interface TypeChangeFieldListProps {
  fields: FieldChange[];
  decisions: Map<string, ExtendedDecision>;
  onDecision: (fieldId: string, decision: ExtendedDecision) => void;
}

function TypeChangeFieldList({ fields, decisions, onDecision }: TypeChangeFieldListProps) {
  return (
    <div>
      <div className="text-xs font-semibold text-muted-foreground mb-2">Type Changes</div>
      <div className="space-y-2">
        {fields.map(field => {
          const currentDecision = decisions.get(field.fieldId) || '';
          const currentValue = field.currentValue;
          const hasValue = currentValue !== undefined && currentValue !== null && currentValue !== '';
          
          // Check if this is a types→text conversion (shows special options)
          const isTypesToText = isTypesToTextConversion(field.oldType || '', field.newType || '');

          return (
            <div
              key={field.fieldId}
              className="text-sm p-3 rounded border bg-amber-500/10 border-amber-500/30"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="font-medium text-amber-700">{field.fieldLabel}</div>
                  <div className="text-xs text-amber-600 mt-1">
                    <span className="font-medium">{field.oldType}</span>
                    <span className="mx-1">→</span>
                    <span className="font-medium">{field.newType}</span>
                  </div>
                  {hasValue && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Current value: <span className="font-mono bg-muted px-1 rounded">{String(currentValue).substring(0, 50)}{String(currentValue).length > 50 ? '...' : ''}</span>
                    </div>
                  )}
                  {isTypesToText && (
                    <div className="text-xs text-blue-600 mt-1">
                      Type → Text: Choose to use the element ID or look up the element name
                    </div>
                  )}
                </div>
                <div className="flex-shrink-0">
                  <Select 
                    value={currentDecision} 
                    onValueChange={(v: string) => onDecision(field.fieldId, v as ExtendedDecision)}
                  >
                    <SelectTrigger className={`w-[180px] border rounded text-xs h-8 ${!currentDecision ? 'border-amber-500 bg-amber-50' : 'bg-background'}`}>
                      <SelectValue placeholder="Select action..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="clear">Clear (use default)</SelectItem>
                      {isTypesToText ? (
                        <>
                          <SelectItem value="convert_id">Use element ID</SelectItem>
                          <SelectItem value="convert_name">Use element name</SelectItem>
                        </>
                      ) : (
                        <SelectItem value="convert">Attempt conversion</SelectItem>
                      )}
                      <SelectItem value="keep">Keep original value</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Step 3: Set Status
interface StatusStepProps {
  availableStatuses: Array<{ key: string; color: string }>;
  selectedStatus: string;
  onStatusChange: (status: string) => void;
}

function StatusStep({ availableStatuses, selectedStatus, onStatusChange }: StatusStepProps) {
  return (
    <div className="space-y-4">
      <div className="p-4 rounded bg-muted/50 border">
        <div className="font-medium mb-1">Select a status for updated objects</div>
        <div className="text-sm text-muted-foreground">
          All updated objects will be set to this status. This helps track which objects have been recently synced.
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {availableStatuses.map(status => (
          <button
            key={status.key}
            onClick={() => onStatusChange(status.key)}
            className={`p-4 rounded border-2 transition-all text-left ${
              selectedStatus === status.key
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/50'
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: status.color }}
              />
              <span className="font-medium capitalize">{status.key}</span>
            </div>
          </button>
        ))}
      </div>

      {availableStatuses.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          No status configurations found. Please configure statuses in Settings.
        </div>
      )}
    </div>
  );
}

// Step 4: Confirm
interface ConfirmStepProps {
  objectCount: number;
  totals: { added: number; removed: number; typeChanged: number };
  hasConflicts: boolean;
  conflictCount: number;
  selectedStatus: string;
  statusColor?: string;
}

function ConfirmStep({
  objectCount,
  totals,
  hasConflicts,
  conflictCount,
  selectedStatus,
  statusColor,
}: ConfirmStepProps) {
  return (
    <div className="space-y-4">
      <div className="p-6 rounded border bg-card">
        <h3 className="font-semibold text-lg mb-4">Update Summary</h3>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2 border-b">
            <span className="text-muted-foreground">Objects to update</span>
            <span className="font-medium">{objectCount}</span>
          </div>
          
          <div className="flex items-center justify-between py-2 border-b">
            <span className="text-muted-foreground">New fields to add</span>
            <span className="font-medium text-green-600">+{totals.added}</span>
          </div>
          
          <div className="flex items-center justify-between py-2 border-b">
            <span className="text-muted-foreground">Fields to remove</span>
            <span className="font-medium text-red-600">-{totals.removed}</span>
          </div>
          
          {hasConflicts && (
            <div className="flex items-center justify-between py-2 border-b">
              <span className="text-muted-foreground">Type conflicts handled</span>
              <span className="font-medium text-amber-600">{conflictCount}</span>
            </div>
          )}
          
          <div className="flex items-center justify-between py-2">
            <span className="text-muted-foreground">New status</span>
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: statusColor }}
              />
              <span className="font-medium capitalize">{selectedStatus}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 rounded bg-amber-500/10 border border-amber-500/30">
        <div className="flex items-start gap-3">
          <IconProvider iconstring="fa-exclamation-triangle" className="text-amber-600 mt-0.5" size={16} />
          <div>
            <div className="font-medium text-amber-700">This action cannot be undone</div>
            <div className="text-sm text-amber-600 mt-1">
              Make sure you have saved your template before proceeding. Removed field data will be permanently deleted.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TemplateSyncWizard;
